import { Clear } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Checkbox,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { isArray } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';

import { CustomFieldInnerProps } from './customFieldModels';
import { CustomFieldType } from './types';
import { useCustomField } from './useCustomField';
import { useSxStyles } from './useStyles';

type OptionObject = {
  label: string;
  value: string;
};

export const LegacyCustomField: React.FC<CustomFieldInnerProps> = (props) => {
  const {
    errors,
    validationRules,
    register,
    textAreaRegister,
    setValue,
    idEditMode,
    fieldDetails,
  } = props;
  const {
    formattedValue,
    options,
    inputValue,
    selectedOption,
    handleChange,
    handleClearValue,
  } = useCustomField(props);
  const sxStyles = useSxStyles();

  useEffect(() => {
    if (!idEditMode) return;
    const type = props.fieldDetails.inputType.value;
    if (type === CustomFieldType.DATE) {
      const dateVal = moment(props.fieldDetails.value, 'YYYY-MM-DD').isValid()
        ? moment(moment(props.fieldDetails.value, 'YYYY-MM-DD')).format()
        : '';
      if (!!setValue) {
        setValue(props.fieldDetails.code, dateVal);
      }
    } else if (
      type === CustomFieldType.ENUM ||
      type === CustomFieldType.MULTISELECT
    ) {
      if (!!setValue) {
        setValue(props.fieldDetails.code, props.fieldDetails.value);
      }
    }
  }, [
    idEditMode,
    props.fieldDetails.code,
    props.fieldDetails.value,
    props.fieldDetails.inputType.value,
    setValue,
  ]);

  if (idEditMode) {
    switch (fieldDetails.inputType.value) {
      case CustomFieldType.BOOLEAN: {
        const isDefaultChecked = isArray(fieldDetails.value)
          ? false
          : ['checked', 'true'].includes(fieldDetails.value);

        return (
          <Box>
            <InputLabel>{fieldDetails.label}</InputLabel>
            <Checkbox
              disabled={props.isSaving}
              name={fieldDetails.code}
              inputRef={register}
              defaultChecked={isDefaultChecked}
              onChange={handleChange}
            />
          </Box>
        );
      }
      case CustomFieldType.STRING: {
        const rules =
          validationRules && validationRules[CustomFieldType.STRING]
            ? validationRules[CustomFieldType.STRING]
            : {};
        return (
          <Box>
            <TextField
              type="text"
              margin="dense"
              name={fieldDetails.code}
              disabled={props.isSaving}
              sx={sxStyles.textInput}
              onChange={handleChange}
              label={fieldDetails.label}
              defaultValue={`${fieldDetails.value}`}
              fullWidth
              inputRef={register && register({ ...rules } as HTMLInputElement)}
              error={errors && !!errors[fieldDetails.code]}
              helperText={
                errors && errors[fieldDetails.code]
                  ? errors[fieldDetails.code].message
                  : ' '
              }
            />
          </Box>
        );
      }
      case CustomFieldType.NUMBER: {
        const rules =
          validationRules && validationRules[CustomFieldType.NUMBER]
            ? validationRules[CustomFieldType.NUMBER]
            : {};
        return (
          <Box>
            <TextField
              type="number"
              margin="dense"
              inputProps={{ step: '0.01' }}
              name={fieldDetails.code}
              disabled={props.isSaving}
              sx={sxStyles.textInput}
              inputRef={register && register({ ...rules } as HTMLInputElement)}
              error={errors && !!errors[fieldDetails.code]}
              helperText={
                errors && errors[fieldDetails.code]
                  ? errors[fieldDetails.code].message
                  : ' '
              }
              onChange={handleChange}
              label={fieldDetails.label}
              defaultValue={`${fieldDetails.value}`}
              fullWidth
            />
          </Box>
        );
      }
      case CustomFieldType.TEXT: {
        const rules =
          validationRules && validationRules[CustomFieldType.STRING]
            ? validationRules[CustomFieldType.STRING]
            : {};
        return (
          <Box>
            <InputLabel>{fieldDetails.label}</InputLabel>
            <Box
              component="textarea"
              maxLength={3000}
              disabled={props.isSaving}
              name={fieldDetails.code}
              sx={{ ...sxStyles.textarea, ...sxStyles.fullWidth }}
              ref={
                textAreaRegister &&
                textAreaRegister(rules as HTMLTextAreaElement)
              }
              defaultValue={`${fieldDetails.value}`}
              onChange={handleChange}
            />
            {errors && errors[fieldDetails.code] ? (
              <Typography>{errors[fieldDetails.code].message}</Typography>
            ) : (
              ' '
            )}
          </Box>
        );
      }
      case CustomFieldType.DATE: {
        return (
          <Box
            sx={{
              ...sxStyles.clearableInputContainer,
              ...sxStyles.marginTopOther,
            }}
          >
            <InputLabel style={{ marginRight: 12 }}>
              {fieldDetails.label}
            </InputLabel>
            <DatePicker
              value={!!inputValue ? moment(inputValue) : null}
              inputRef={register}
              onChange={handleChange}
              disabled={props.isSaving}
            />
            <IconButton sx={sxStyles.clearButton} onClick={handleClearValue}>
              <Clear />
            </IconButton>
          </Box>
        );
      }
      case CustomFieldType.ENUM: {
        return (
          <Box sx={sxStyles.marginTopOther}>
            <Autocomplete
              options={options}
              getOptionLabel={(option: OptionObject) => option.label as string}
              value={selectedOption as OptionObject}
              filterSelectedOptions
              disabled={props.isSaving}
              onChange={handleChange}
              sx={sxStyles.fullWidth}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={fieldDetails.label}
                  placeholder=""
                />
              )}
            />
          </Box>
        );
      }
      case CustomFieldType.MULTISELECT: {
        const defautValue = (selectedOption || []) as OptionObject[];
        return (
          <Box sx={sxStyles.marginTopOther}>
            <Autocomplete
              multiple
              options={options}
              getOptionLabel={(option: OptionObject) => option.label as string}
              defaultValue={defautValue}
              filterSelectedOptions
              disabled={props.isSaving}
              onChange={handleChange}
              sx={sxStyles.fullWidth}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={fieldDetails.label}
                  placeholder=""
                />
              )}
            />
          </Box>
        );
      }
      default: {
        return null;
      }
    }
  }

  return <Typography sx={sxStyles.textFieldWrap}>{formattedValue}</Typography>;
};
